import { useEffect, useMemo, useState } from "react";
import { authAxios } from "../../utils/auth";
import { Card } from "../ui/card";
import { Button } from "../ui/button";
import { toast } from "sonner";
import { Sliders, Save, RefreshCw, TrendingUp, TrendingDown, History } from "lucide-react";

const GOLD = "#C5A55A";

export default function RulesPanel() {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [config, setConfig] = useState(null);
  const [defaults, setDefaults] = useState(null);
  const [signalCodes, setSignalCodes] = useState({ guilty: [], innocent: [] });
  const [audit, setAudit] = useState([]);
  const [reason, setReason] = useState("");
  const [preview, setPreview] = useState(null);

  const [weights, setWeights] = useState({});
  const [hardSignals, setHardSignals] = useState([]);
  const [thresholds, setThresholds] = useState({ freeze: 85, prewarn: 70, watch: 40 });
  const [highValueCents, setHighValueCents] = useState(50000);

  const load = async () => {
    setLoading(true);
    try {
      const [r, a] = await Promise.all([
        authAxios.get("/admin/fraud/rules"),
        authAxios.get("/admin/fraud/rules/audit?limit=15"),
      ]);
      setConfig(r.data.config);
      setDefaults(r.data.defaults?.weights || {});
      setSignalCodes(r.data.signal_codes || { guilty: [], innocent: [] });
      setWeights({ ...r.data.config.weights });
      setHardSignals([...(r.data.config.hard_signals || [])]);
      setThresholds({ ...r.data.config.thresholds });
      setHighValueCents(r.data.config.high_value_cents);
      setAudit(a.data.rows || []);
    } catch (err) {
      toast.error("Failed to load Rules config");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const dirty = useMemo(() => {
    if (!config) return false;
    if (JSON.stringify(weights) !== JSON.stringify(config.weights)) return true;
    if (JSON.stringify(hardSignals.slice().sort()) !== JSON.stringify((config.hard_signals || []).slice().sort())) return true;
    if (JSON.stringify(thresholds) !== JSON.stringify(config.thresholds)) return true;
    if (highValueCents !== config.high_value_cents) return true;
    return false;
  }, [config, weights, hardSignals, thresholds, highValueCents]);

  const resetToDefaults = () => {
    if (!defaults) return;
    if (!window.confirm("Reset every weight to shipped defaults? (You still need to Save to apply.)")) return;
    setWeights({ ...defaults });
  };

  const revert = () => {
    if (!config) return;
    setWeights({ ...config.weights });
    setHardSignals([...(config.hard_signals || [])]);
    setThresholds({ ...config.thresholds });
    setHighValueCents(config.high_value_cents);
    setPreview(null);
  };

  const runPreview = async () => {
    setPreviewing(true);
    try {
      const r = await authAxios.post("/admin/fraud/rules/preview", {
        weights, hard_signals: hardSignals, thresholds, days: 30,
      });
      setPreview(r.data);
    } catch (err) {
      toast.error("Preview failed");
    } finally {
      setPreviewing(false);
    }
  };

  const save = async () => {
    if (!window.confirm("Apply these rule changes to production scoring?")) return;
    setSaving(true);
    try {
      await authAxios.put("/admin/fraud/rules", {
        weights, hard_signals: hardSignals, thresholds,
        high_value_cents: highValueCents, reason,
      });
      toast.success("Rules saved · new scores use these weights immediately");
      setReason("");
      setPreview(null);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !config) {
    return <div className="text-white/50 text-sm">Loading rules…</div>;
  }

  return (
    <section data-testid="rules-panel" className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-white font-semibold flex items-center gap-2">
          <Sliders className="w-4 h-4" style={{ color: GOLD }} /> Rules Engine
          <span className="text-white/40 text-[10px]">Live · updated {config.updated_at?.slice(0, 10)} by {(config.updated_by || "system").slice(0, 18)}</span>
        </h3>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={resetToDefaults} className="border-white/10 text-white/60" data-testid="rules-reset-defaults">
            <RefreshCw className="w-3 h-3 mr-1" /> Reset to defaults
          </Button>
          <Button size="sm" variant="outline" onClick={revert} disabled={!dirty} className="border-white/10 text-white/60" data-testid="rules-revert">
            Revert
          </Button>
        </div>
      </div>

      {/* Thresholds + High-value */}
      <Card className="bg-[#0a0a0f] border-white/5 p-5">
        <div className="text-white/40 text-[10px] uppercase mb-3">Tier thresholds & high-value hold</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {["freeze", "prewarn", "watch"].map((k) => (
            <label key={k} className="text-xs text-white/70">
              <span className="block mb-1 uppercase text-[10px] text-white/40">{k} ≥</span>
              <input
                type="number" min="0" max="100"
                value={thresholds[k]}
                onChange={(e) => setThresholds({ ...thresholds, [k]: Number(e.target.value) })}
                className="w-full bg-black/40 border border-white/10 rounded p-2 text-white text-sm font-mono"
                data-testid={`threshold-${k}`}
              />
            </label>
          ))}
          <label className="text-xs text-white/70">
            <span className="block mb-1 uppercase text-[10px] text-white/40">High-value hold ($)</span>
            <input
              type="number" min="100" step="50"
              value={Math.floor(highValueCents / 100)}
              onChange={(e) => setHighValueCents(Math.max(10000, Number(e.target.value) * 100))}
              className="w-full bg-black/40 border border-white/10 rounded p-2 text-white text-sm font-mono"
              data-testid="high-value-cents"
            />
          </label>
        </div>
      </Card>

      {/* Weights table */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <WeightsBlock
          title="Guilty signals (positive → riskier)"
          codes={signalCodes.guilty}
          weights={weights}
          setWeights={setWeights}
          defaults={defaults || {}}
          hardSignals={hardSignals}
          setHardSignals={setHardSignals}
          color="text-red-400"
          testIdPrefix="guilty"
        />
        <WeightsBlock
          title="Innocent signals (negative → trust)"
          codes={signalCodes.innocent}
          weights={weights}
          setWeights={setWeights}
          defaults={defaults || {}}
          hardSignals={null}
          color="text-emerald-400"
          testIdPrefix="innocent"
        />
      </div>

      {/* Save row */}
      <Card className="bg-[#0a0a0f] border-white/5 p-4">
        <div className="flex flex-col md:flex-row md:items-center gap-3">
          <input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Change note (audit log) — e.g. 'Bumped shared_card_fp after Case fc_ab12'"
            className="flex-1 bg-black/40 border border-white/10 rounded p-2 text-xs text-white"
            data-testid="rules-reason"
          />
          <Button size="sm" onClick={runPreview} disabled={!dirty || previewing} variant="outline" className="border-cyan-500/30 text-cyan-300 gap-1.5" data-testid="rules-preview">
            <RefreshCw className={`w-3.5 h-3.5 ${previewing ? "animate-spin" : ""}`} /> Preview last 30d
          </Button>
          <Button size="sm" onClick={save} disabled={!dirty || saving} className="bg-emerald-500 hover:bg-emerald-400 text-black gap-1.5" data-testid="rules-save">
            <Save className="w-3.5 h-3.5" /> Save
          </Button>
        </div>
      </Card>

      {/* Preview panel */}
      {preview && (
        <Card className="bg-[#0a0a0f] border-cyan-500/20 p-5" data-testid="preview-result">
          <div className="text-cyan-300 text-xs uppercase mb-3">Preview · replay of last {preview.days}d</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <PreviewStat label="Scanned" value={preview.scanned} />
            <PreviewStat label="Changed" value={preview.changed} color="text-amber-300" />
            <PreviewStat label="Would upgrade" value={preview.upgraded} color="text-red-400" icon={TrendingUp} />
            <PreviewStat label="Would downgrade" value={preview.downgraded} color="text-emerald-400" icon={TrendingDown} />
          </div>
          {preview.sample?.length > 0 && (
            <div>
              <div className="text-white/40 text-[10px] uppercase mb-2">Sample changes</div>
              <table className="w-full text-xs" data-testid="preview-sample">
                <thead className="text-white/40 uppercase text-[9px]">
                  <tr>
                    <th className="text-left py-1">Case</th>
                    <th className="text-left">Old tier</th>
                    <th className="text-left">New tier</th>
                    <th className="text-right">Old score</th>
                    <th className="text-right">New score</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.sample.map((r) => (
                    <tr key={r.case_id} className="border-t border-white/[0.04] text-white/70">
                      <td className="py-1 font-mono">{r.case_id.slice(0, 12)}</td>
                      <td>{r.old_tier}</td>
                      <td className="text-cyan-300">{r.new_tier}</td>
                      <td className="text-right font-mono">{r.old_score}</td>
                      <td className="text-right font-mono text-cyan-300">{r.new_score}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {preview.scanned === 0 && (
            <div className="text-white/40 text-xs">No historical cases in the last 30 days to replay against.</div>
          )}
        </Card>
      )}

      {/* Audit log */}
      <Card className="bg-[#0a0a0f] border-white/5 p-5" data-testid="rules-audit">
        <div className="text-white/80 text-xs font-semibold mb-3 flex items-center gap-2">
          <History className="w-3.5 h-3.5" style={{ color: GOLD }} /> Audit log
        </div>
        {audit.length === 0 && <div className="text-white/30 text-xs">No changes yet.</div>}
        <div className="space-y-3 max-h-96 overflow-y-auto">
          {audit.map((r, i) => {
            const changes = diffSummary(r.before, r.after);
            return (
              <div key={i} className="border-l-2 border-white/10 pl-3 text-xs">
                <div className="flex items-center gap-2">
                  <span className="text-white/50 font-mono text-[10px]">{r.at?.slice(0, 19).replace("T", " ")}</span>
                  <span className="text-white/80">{r.admin_user_id}</span>
                </div>
                {r.reason && <div className="text-white/60 italic mt-1">"{r.reason}"</div>}
                <div className="text-white/50 text-[10px] mt-1">{changes || "(no visible changes)"}</div>
              </div>
            );
          })}
        </div>
      </Card>
    </section>
  );
}

function WeightsBlock({ title, codes, weights, setWeights, defaults, hardSignals, setHardSignals, color, testIdPrefix }) {
  return (
    <Card className="bg-[#0a0a0f] border-white/5 p-4" data-testid={`weights-${testIdPrefix}`}>
      <div className={`text-[10px] uppercase mb-3 ${color}`}>{title}</div>
      <div className="space-y-2">
        {codes.map((code) => {
          const cur = weights[code] ?? 0;
          const def = defaults[code] ?? 0;
          const changed = cur !== def;
          return (
            <div key={code} className="flex items-center gap-2 text-xs">
              <span className="font-mono text-white/85 flex-1 truncate" title={code}>{code}</span>
              {hardSignals !== null && (
                <label className="flex items-center gap-1 text-white/50 text-[10px]" title="Hard signal: 2+ trip auto-freeze">
                  <input
                    type="checkbox"
                    checked={hardSignals.includes(code)}
                    onChange={(e) => {
                      if (e.target.checked) setHardSignals([...hardSignals, code]);
                      else setHardSignals(hardSignals.filter((c) => c !== code));
                    }}
                    data-testid={`hard-${code}`}
                  />
                  hard
                </label>
              )}
              <input
                type="number" min="-100" max="100"
                value={cur}
                onChange={(e) => setWeights({ ...weights, [code]: Number(e.target.value) })}
                className={`w-16 bg-black/40 border rounded p-1 font-mono text-xs text-right ${changed ? "border-amber-500/50 text-amber-300" : "border-white/10 text-white/80"}`}
                data-testid={`weight-${code}`}
              />
              {changed && <span className="text-white/30 text-[10px] w-10 text-right" title="Default">({def})</span>}
              {!changed && <span className="w-10" />}
            </div>
          );
        })}
      </div>
    </Card>
  );
}

function PreviewStat({ label, value, color = "text-white/90", icon: Icon }) {
  return (
    <div className="bg-black/30 rounded p-3 border border-white/5">
      <div className="text-[10px] text-white/40 uppercase flex items-center gap-1">
        {Icon && <Icon className="w-3 h-3" />} {label}
      </div>
      <div className={`text-xl font-mono mt-1 ${color}`}>{value}</div>
    </div>
  );
}

function diffSummary(before, after) {
  if (!before || !after) return "";
  const parts = [];
  const bw = before.weights || {};
  const aw = after.weights || {};
  const keys = new Set([...Object.keys(bw), ...Object.keys(aw)]);
  for (const k of keys) {
    if ((bw[k] ?? 0) !== (aw[k] ?? 0)) parts.push(`${k}: ${bw[k] ?? 0}→${aw[k] ?? 0}`);
  }
  if (JSON.stringify(before.thresholds) !== JSON.stringify(after.thresholds)) {
    parts.push(`thresholds: ${JSON.stringify(before.thresholds)}→${JSON.stringify(after.thresholds)}`);
  }
  if ((before.high_value_cents ?? 0) !== (after.high_value_cents ?? 0)) {
    parts.push(`high_value_cents: ${before.high_value_cents}→${after.high_value_cents}`);
  }
  if (JSON.stringify((before.hard_signals || []).slice().sort()) !== JSON.stringify((after.hard_signals || []).slice().sort())) {
    parts.push(`hard_signals updated`);
  }
  return parts.slice(0, 6).join(" · ");
}
